import { ComponentPropsWithRef, forwardRef, Ref } from "react";
import { Link } from "react-router-dom";
import { getUrlPath } from "../utils/url.ts";
import OffScreen from "./OffScreen.tsx";
import { FormattedMessage } from "react-intl";

type AnchorProps = ComponentPropsWithRef<"a">;
type ButtonProps = ComponentPropsWithRef<"button">;
type LinkProps = ComponentPropsWithRef<typeof Link>;

export type BaseLinkOrButtonProps = AnchorProps | ButtonProps | LinkProps;

function getTarget(href: string, propTarget?: string) {
  if (propTarget) {
    return propTarget;
  }

  if (getUrlPath(href).startsWith("/reader/books")) {
    return "_self";
  }
  return "_blank";
}

function BaseLinkOrButton(
  props: BaseLinkOrButtonProps,
  ref: Ref<HTMLButtonElement | HTMLAnchorElement>
) {
  if ("to" in props && props.to) {
    const { to, ...rest } = props;
    return <Link ref={ref as Ref<HTMLAnchorElement>} to={to} {...rest} />;
  }

  if ("href" in props && props.href) {
    const { children, target, ...rest } = props;
    const linkTarget = getTarget(props.href, target);
    return (
      <a
        ref={ref as Ref<HTMLAnchorElement>}
        target={linkTarget}
        rel="noopener noreferrer"
        {...rest}
      >
        {children}
        {linkTarget !== "_self" && (
          <OffScreen tagName="span">
            <FormattedMessage defaultMessage="(opens in a new window)" />
          </OffScreen>
        )}
      </a>
    );
  }

  return (
    <button
      ref={ref as Ref<HTMLButtonElement>}
      type="button"
      {...(props as ButtonProps)}
    />
  );
}

const ForwardRef = forwardRef(BaseLinkOrButton);
export default ForwardRef;
